// ResponseSetPicker — the "Response set" dropdown above the checkList options
// table: a compact trigger (picked set name / placeholder + chevron) opening a
// searchable list of the org's reusable response sets (GET /form-choices/all),
// with a trailing "Add response set" row that opens ResponseSetModal in create
// mode (no `set` prop).
//
// Picking a set COPIES its choices into the field's optionList — nothing is
// persisted about the set itself, which is why the Options table below reads
// "fine-tune": the copy is the user's to edit. Consequence: the picked name is
// LOCAL state only, so the trigger falls back to the placeholder on remount
// rather than claiming a set the model can't confirm.
//
// The list query is `skip`ped while closed — the picker mounts for every
// selected checkList field, and the sets are only needed once it opens.
// ResponseSetModal's create invalidates FormChoices/LIST, so a newly created
// set shows up here without any refetch of our own.
//
// The WHOLE picker rides the `responses_access` extra (user decision,
// 2026-09-14): without it nothing renders — not even the read list, so set
// names don't leak — and the field's options stay hand-authored via the
// Options table below.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Input } from '@/components/common';
import { useCanAccessFormResponses } from '@/hooks/useModuleAccess';
import { useGetFormChoicesQuery, type ResponseSet } from '@/api/endpoints/formChoicesApi';
import type { ChoiceOption } from '../utils/formElementDefaults';
import ResponseSetModal from './ResponseSetModal';

interface ResponseSetPickerProps {
  /** Apply a set — its choices REPLACE the field's optionList (no prompt). */
  onPick: (options: ChoiceOption[]) => void;
}

/** ResponseSetChoice → ChoiceOption: the same three things under different key
 *  names. Both models store 8-digit hex, so the color passes straight through. */
const toOptions = (set: ResponseSet): ChoiceOption[] =>
  set.choices.map((c) => ({ responseText: c.label, value: c.value, itemColor: c.color }));

const ResponseSetPicker = ({ onPick }: ResponseSetPickerProps) => {
  const { t } = useTranslation();
  const canAccessResponses = useCanAccessFormResponses();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  // Name, not id: the list query is skipped while closed, so an id would have
  // nothing to resolve against once the panel shuts.
  const [pickedName, setPickedName] = useState('');

  const { data: sets = [] } = useGetFormChoicesQuery(undefined, { skip: !open });

  if (!canAccessResponses) return null;

  const q = search.trim().toLowerCase();
  const shown = q ? sets.filter((s) => s.name.toLowerCase().includes(q)) : sets;

  const pick = (set: ResponseSet) => {
    setPickedName(set.name);
    onPick(toOptions(set));
    setOpen(false);
  };

  return (
    <>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='start'
        width={272}
        // The properties panel is `overflow-y: auto`, so an in-flow panel gets
        // clipped by it — portal to the body and anchor with fixed coords.
        portal
        className='form-builder-props__rs'
        trigger={
          <button
            type='button'
            className='form-builder-props__rs-trigger'
            aria-expanded={open}
            onClick={() => {
              setOpen((o) => !o);
              setSearch('');
            }}
          >
            <span
              className={[
                'form-builder-props__rs-name',
                !pickedName && 'form-builder-props__rs-placeholder',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {pickedName || t('forms_builder_props_response_set_ph')}
            </span>
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={15} />
          </button>
        }
      >
        <div className='form-builder-props__rs-panel'>
          <Input
            size='sm'
            iconStart='search'
            placeholder={t('forms_builder_props_response_set_search')}
            aria-label={t('forms_builder_props_response_set_search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className='form-builder-props__rs-list'>
            {shown.length === 0 ? (
              <span className='form-builder-props__rs-empty'>
                {t('forms_builder_props_response_set_empty')}
              </span>
            ) : (
              shown.map((set) => (
                <button
                  key={set.id}
                  type='button'
                  className='app-dropdown__item form-builder-props__rs-row'
                  role='menuitem'
                  onClick={() => pick(set)}
                >
                  <span className='app-dropdown__item-label'>{set.name}</span>
                </button>
              ))
            )}
          </div>

          <button
            type='button'
            className='form-builder-props__rs-add'
            onClick={() => {
              setOpen(false);
              setAddOpen(true);
            }}
          >
            <Icon name='plus' size={15} />
            {t('forms_builder_props_response_set_add')}
          </button>
        </div>
      </Dropdown>

      <ResponseSetModal open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
};

export default ResponseSetPicker;
